import { HOME_WIDGET_TRANSITION_MS } from './widgetLayoutConstants';

export type WidgetRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

const FLIP_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

/** Measure in-flow animated widget slots (skips exit overlays). */
export const measureWidgetRects = (
  container: HTMLElement
): Map<string, WidgetRect> => {
  const elements = container.querySelectorAll<HTMLElement>(
    '[data-layout-animated][data-widget-id]:not([data-layout-exiting="true"])'
  );
  const nextPositions = new Map<string, WidgetRect>();

  elements.forEach((element) => {
    const widgetId = element.getAttribute('data-widget-id');
    if (!widgetId) {
      return;
    }

    const rect = element.getBoundingClientRect();
    nextPositions.set(widgetId, {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
  });

  return nextPositions;
};

const clearFlipStyles = (element: HTMLElement) => {
  element.style.willChange = '';
  element.style.transition = '';
  element.style.transform = '';
  element.style.transformOrigin = '';
};

/**
 * FLIP Play: invert from → to with translate + scale, then animate to identity.
 * Size-aware so 33%→50% / full→half never rely on competing CSS flex tweens.
 */
export const playFlip = (
  elements: HTMLElement[],
  from: Map<string, WidgetRect>,
  to: Map<string, WidgetRect>,
  durationMs: number = HOME_WIDGET_TRANSITION_MS
): void => {
  const FLIP_TRANSITION = `transform ${durationMs}ms ${FLIP_EASING}`;

  elements.forEach((element) => {
    const widgetId = element.getAttribute('data-widget-id');
    if (!widgetId) {
      return;
    }

    const previousRect = from.get(widgetId);
    const nextRect = to.get(widgetId);
    if (!previousRect || !nextRect) {
      return;
    }

    const deltaX = previousRect.left - nextRect.left;
    const deltaY = previousRect.top - nextRect.top;
    const scaleX =
      nextRect.width > 0.5 ? previousRect.width / nextRect.width : 1;
    const scaleY =
      nextRect.height > 0.5 ? previousRect.height / nextRect.height : 1;

    const hasTranslate = Math.abs(deltaX) >= 0.5 || Math.abs(deltaY) >= 0.5;
    const hasScale =
      Math.abs(scaleX - 1) >= 0.01 || Math.abs(scaleY - 1) >= 0.01;

    if (!hasTranslate && !hasScale) {
      return;
    }

    element.style.willChange = 'transform';
    element.style.transformOrigin = 'top left';
    element.style.transition = 'none';
    element.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        element.style.transition = FLIP_TRANSITION;
        element.style.transform = '';

        const handleTransitionEnd = (event: TransitionEvent) => {
          if (event.target !== element || event.propertyName !== 'transform') {
            return;
          }
          clearFlipStyles(element);
          element.removeEventListener('transitionend', handleTransitionEnd);
        };

        element.addEventListener('transitionend', handleTransitionEnd);
      });
    });
  });
};
